// Client for the auth service's standalone-signup endpoints (outside Better
// Auth's /api/auth namespace): live address availability and provisioning status.

import { authServiceOrigin } from '@/lib/auth-client'

import {
  CREATE_APP_PASSWORD_REASONS,
  type CreateAppPasswordReason,
  REVOKE_APP_PASSWORD_REASONS,
  type RevokeAppPasswordReason,
  readFailureReason,
} from './app-password-failure'

export type AvailabilityReason = 'invalid' | 'reserved' | 'taken'

export interface AvailabilityResult {
  available: boolean
  reason?: AvailabilityReason
}

export type MailboxStatus = 'pending' | 'active' | 'failed' | 'none'

// Settings read model: the current address, the retained aliases that still
// receive mail, how many renames remain, and — for standalone users — whether
// changing the address also changes the login identifier.
export interface MailboxInfo {
  address: string
  loginWillChange: boolean
  previousAddresses: string[]
  renamesRemaining: number
}

// Fetch the signed-in user's mailbox info (cookie-authenticated). Returns null
// when the user has no active native mailbox yet.
export async function getMailboxInfo(): Promise<MailboxInfo | null> {
  const res = await fetch(`${authServiceOrigin}/native-mailbox/info`, { credentials: 'include' })
  if (res.status === 404) {
    return null
  }
  if (!res.ok) {
    throw new Error(`mailbox info failed: ${res.status}`)
  }
  return (await res.json()) as MailboxInfo
}

// The iOS configuration-profile download.
//
// This URL must be opened by a TOP-LEVEL NAVIGATION, never fetched: iOS only
// offers to install a configuration profile when the file arrives as a
// navigation in Safari. A fetch() + blob download hands the user an inert file
// the profile installer never sees. The session cookie rides along because it
// is scoped to `.langmail.me` and a top-level GET carries SameSite cookies.
export const appleProfileUrl = `${authServiceOrigin}/native-mailbox/apple-profile`

export type ChangeAddressReason =
  | 'cap_reached'
  | 'invalid'
  | 'not_provisioned'
  | 'reserved'
  | 'taken'
  | 'unavailable'
  | 'unchanged'

export type ChangeAddressResult = { address: string; ok: true } | { ok: false; reason: ChangeAddressReason }

// Commit an in-place address change (cookie-authenticated). The response reason
// is the authoritative answer — availability checked as the user types is only
// advisory, so a `taken` here means the name was claimed in the race.
export async function changeMailboxAddress(username: string): Promise<ChangeAddressResult> {
  const res = await fetch(`${authServiceOrigin}/native-mailbox/change-address`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username }),
  })
  if (res.ok) {
    return { ok: true, address: ((await res.json()) as { address: string }).address }
  }
  const body = (await res.json().catch(() => ({}))) as { reason?: ChangeAddressReason }
  return { ok: false, reason: body.reason ?? 'invalid' }
}

// Mail-client app passwords on the user's Langmail mailbox
// (cookie-authenticated). Stalwart owns the list; the sync credential Langmail
// itself uses is hidden but still occupies a slot, reported as `reserved`, so
// the slots the user can fill are `limit - reserved`.

// `apple-profile` is the credential the iPhone/iPad profile download minted.
type AppPasswordKind = 'apple-profile' | 'user'

export interface AppPassword {
  description: string
  id: string
  kind: AppPasswordKind
}

export interface AppPasswordList {
  limit: number
  passwords: AppPassword[]
  reserved: number
}

// Everything a mail client needs. `secret` is returned exactly once, here.
export interface AppPasswordCredential {
  id: string
  imapPort: number
  secret: string
  server: string
  smtpPort: number
  username: string
}

export type CreateAppPasswordResult =
  | { credential: AppPasswordCredential; ok: true }
  | { ok: false; reason: CreateAppPasswordReason }

export type RevokeAppPasswordResult = { ok: true } | { ok: false; reason: RevokeAppPasswordReason }

const appPasswordsUrl = `${authServiceOrigin}/native-mailbox/app-passwords`

export async function listAppPasswords(): Promise<AppPasswordList> {
  const res = await fetch(appPasswordsUrl, { credentials: 'include' })
  if (!res.ok) {
    throw new Error(`app passwords list failed: ${res.status}`)
  }
  return (await res.json()) as AppPasswordList
}

// The server refuses a bad description with `invalid_description`; callers
// still validate first so the user gets the precise reason inline.
export async function createAppPassword(description: string): Promise<CreateAppPasswordResult> {
  const res = await fetch(appPasswordsUrl, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ description }),
  })
  if (res.ok) {
    return { ok: true, credential: (await res.json()) as AppPasswordCredential }
  }
  return { ok: false, reason: await readFailureReason(res, CREATE_APP_PASSWORD_REASONS) }
}

export async function revokeAppPassword(id: string): Promise<RevokeAppPasswordResult> {
  const res = await fetch(`${appPasswordsUrl}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (res.ok) {
    return { ok: true }
  }
  return { ok: false, reason: await readFailureReason(res, REVOKE_APP_PASSWORD_REASONS) }
}

// Check whether a desired @langmail.me local part is free. Unauthenticated.
export async function checkAddressAvailability(username: string, signal?: AbortSignal): Promise<AvailabilityResult> {
  const url = `${authServiceOrigin}/native-mailbox/availability?username=${encodeURIComponent(username)}`
  const res = await fetch(url, { signal })
  if (!res.ok) {
    throw new Error(`availability check failed: ${res.status}`)
  }
  return (await res.json()) as AvailabilityResult
}

// The signed-in user's native-mailbox provisioning status (cookie-authenticated).
// Internal to the poll loop below — callers use waitForMailboxActive.
async function getMailboxStatus(): Promise<MailboxStatus> {
  const res = await fetch(`${authServiceOrigin}/native-mailbox/status`, {
    credentials: 'include',
  })
  if (!res.ok) {
    throw new Error(`mailbox status failed: ${res.status}`)
  }
  return ((await res.json()) as { status: MailboxStatus }).status
}

const POLL_INTERVAL_MS = 1000
const POLL_TIMEOUT_MS = 30_000

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export type MailboxProvisionOutcome = 'active' | 'failed' | 'timeout'

// Poll mailbox status after sign-up until it resolves. Standalone login and
// mailbox are one identity (Decision 6), so we don't land the user until the
// mailbox is `active` — a short spinner rather than a pending state.
export async function waitForMailboxActive(): Promise<MailboxProvisionOutcome> {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  while (Date.now() < deadline) {
    try {
      const status = await getMailboxStatus()
      if (status === 'active') {
        return 'active'
      }
      if (status === 'failed') {
        return 'failed'
      }
    } catch {
      // Transient failure (e.g. a brief auth 502 during a deploy) — keep polling
      // until the deadline rather than rejecting and stranding the signup form.
    }
    await sleep(POLL_INTERVAL_MS)
  }
  return 'timeout'
}
